// In-app Support page — logged-in users submit support tickets here AND admins
// see the inbox. Two tabs: "Submit a Request" + "Inbox" (admins only).

import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  MessageCircleQuestion, Inbox, Loader2, ChevronRight, ExternalLink, Filter, AlertCircle,
} from 'lucide-react';
import SupportRequestForm from '@/components/support/SupportRequestForm';
import BackButton from '@/components/shared/BackButton';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOURS = {
  'New': 'bg-blue-50 text-blue-700 border-blue-200',
  'In Progress': 'bg-amber-50 text-amber-700 border-amber-200',
  'Awaiting Customer': 'bg-purple-50 text-purple-700 border-purple-200',
  'Resolved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Closed': 'bg-stone-50 text-stone-700 border-stone-200',
};

const ADMIN_ROLES = ['super_admin', 'owner', 'org_owner', 'admin', 'practice_manager', 'org_admin'];

export default function InAppSupportPage() {
  const { user, token } = useAuth();
  const canSeeInbox = ADMIN_ROLES.includes(user?.role);
  const [tab, setTab] = useState('submit');

  return (
    <div className="max-w-5xl mx-auto p-6 sm:p-8" data-testid="in-app-support-page">
      <BackButton fallback="/app" className="mb-4" testId="in-app-support-back" />
      <div className="mb-6">
        <h1 className="text-3xl font-light text-[#2C2420] mb-1">Support</h1>
        <p className="text-sm text-[#5D524D]">Need help, found a bug, or have a feature idea? Send it here.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E6E0D9] p-1" data-testid="in-app-support-tabs">
          <TabsTrigger value="submit" className="gap-2 data-[state=active]:bg-[#4A3B32] data-[state=active]:text-white" data-testid="in-app-support-tab-submit">
            <MessageCircleQuestion className="w-4 h-4" /> Submit a Request
          </TabsTrigger>
          {canSeeInbox && (
            <TabsTrigger value="inbox" className="gap-2 data-[state=active]:bg-[#4A3B32] data-[state=active]:text-white" data-testid="in-app-support-tab-inbox">
              <Inbox className="w-4 h-4" /> Inbox
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="submit" className="mt-6">
          <SupportRequestForm
            mode="in_app"
            token={token}
            prefill={{ name: user?.name, email: user?.email, companyName: user?.organisation?.name }}
          />
        </TabsContent>

        {canSeeInbox && (
          <TabsContent value="inbox" className="mt-6">
            <SupportInbox token={token} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}


function SupportInbox({ token }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], counts: { total: 0, new: 0, inProgress: 0, resolved: 0 } });
  const [statusFilter, setStatusFilter] = useState('all');
  const [issueTypeFilter, setIssueTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (issueTypeFilter !== 'all') params.set('issueType', issueTypeFilter);
      const res = await fetch(`${API_BASE}/support-requests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('inbox load failed');
      setData(await res.json());
    } catch (e) {
      logger.warn('SupportInbox: load failed', e);
      toast.error('Could not load support inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, issueTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = data.counts || { total: 0, new: 0, inProgress: 0, resolved: 0 };

  return (
    <div className="space-y-4">
      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <CountTile label="Total" value={counts.total} testid="inbox-count-total" />
        <CountTile label="New" value={counts.new} accent="text-blue-700" testid="inbox-count-new" />
        <CountTile label="In progress" value={counts.inProgress} accent="text-amber-700" testid="inbox-count-in-progress" />
        <CountTile label="Resolved" value={counts.resolved} accent="text-emerald-700" testid="inbox-count-resolved" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-[#8C8078]" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-8" data-testid="inbox-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['New', 'In Progress', 'Awaiting Customer', 'Resolved', 'Closed'].map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={issueTypeFilter} onValueChange={setIssueTypeFilter}>
          <SelectTrigger className="w-[200px] h-8" data-testid="inbox-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All issue types</SelectItem>
            {['Bug Report', 'Feature Request', 'General Support', 'Account Help'].map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data.supportInbox && (
          <span className="text-xs text-[#8C8078] ml-auto" data-testid="inbox-email-destination">
            Notifications routed to <strong className="font-mono text-[#5D524D]">{data.supportInbox}</strong>
          </span>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-[#5D524D]" data-testid="inbox-loading">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-center text-[#5D524D] py-16 border border-dashed border-[#E6E0D9] rounded-lg bg-white" data-testid="inbox-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-[#A86A3A]" />
          <p className="text-sm">No support requests {statusFilter !== 'all' || issueTypeFilter !== 'all' ? 'match your filters' : 'yet'}.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="inbox-items">
          {data.items.map(item => (
            <button
              key={item.id}
              onClick={() => setSelected(item)}
              className="w-full text-left bg-white border border-[#E6E0D9] rounded-lg p-4 hover:border-[#A86A3A] transition-colors flex items-start gap-3"
              data-testid={`inbox-item-${item.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-xs text-[#8C8078]">{item.ticketNumber}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#F9F7F5] text-[#5D524D] border border-[#E6E0D9]">{item.issueType}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLOURS[item.status] || ''}`}>{item.status}</span>
                  {item.source === 'public_site' && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F5EDE4] text-[#A86A3A] border border-[#E6E0D9]">Public</span>
                  )}
                </div>
                <p className="text-sm text-[#2C2420] line-clamp-2 mb-1">{item.description}</p>
                <p className="text-xs text-[#8C8078]">
                  {item.submitterName || item.submitterEmail} · {new Date(item.createdAt).toLocaleString()}
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#8C8078] mt-1 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <SupportDetailModal
        item={selected}
        onClose={() => setSelected(null)}
        token={token}
        onUpdated={() => { setSelected(null); load(); }}
      />
    </div>
  );
}


function CountTile({ label, value, accent = 'text-[#2C2420]', testid }) {
  return (
    <div className="bg-white border border-[#E6E0D9] rounded-lg p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#8C8078]">{label}</p>
      <p className={`text-2xl font-light ${accent}`}>{value}</p>
    </div>
  );
}


function SupportDetailModal({ item, onClose, token, onUpdated }) {
  const [status, setStatus] = useState(item?.status || 'New');
  const [notes, setNotes] = useState(item?.internalNotes || '');
  const [saving, setSaving] = useState(false);

  // Reset whenever a new ticket is selected
  useEffect(() => {
    if (item) {
      setStatus(item.status);
      setNotes(item.internalNotes || '');
    }
  }, [item]);

  if (!item) return null;

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/support-requests/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, internalNotes: notes }),
      });
      if (!res.ok) { toast.error('Update failed'); setSaving(false); return; }
      toast.success('Ticket updated');
      onUpdated?.();
    } catch (e) {
      logger.warn('SupportDetailModal: save failed', e);
      toast.error('Network error');
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="inbox-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-[#8C8078]">{item.ticketNumber}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#F9F7F5] border border-[#E6E0D9]">{item.issueType}</span>
          </DialogTitle>
          <DialogDescription>
            From <strong>{item.submitterName || 'Unknown'}</strong> ({item.submitterEmail})
            {item.companyName && <> · {item.companyName}</>}
            <> · {new Date(item.createdAt).toLocaleString()}</>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Section title="Description">{item.description}</Section>
          {item.featureIdea && <Section title="Feature idea">{item.featureIdea}</Section>}
          {item.currentProcess && <Section title="Current process / pain points">{item.currentProcess}</Section>}
          {item.featureImpact && <Section title="Feature impact">{item.featureImpact}</Section>}
          {item.relatedUrl && (
            <Section title="Related URL">
              <a href={item.relatedUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#A86A3A] underline">
                {item.relatedUrl}
                <ExternalLink className="w-3 h-3" />
              </a>
            </Section>
          )}

          <div className="border-t border-[#E6E0D9] pt-4 space-y-3" data-testid="inbox-detail-admin-actions">
            <div>
              <label className="text-xs text-[#8C8078] block mb-1">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="inbox-detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['New', 'In Progress', 'Awaiting Customer', 'Resolved', 'Closed'].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#8C8078] block mb-1">Internal notes (not shared with customer)</label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Investigation notes, links to fixes, who's on it…"
                className="min-h-[80px]"
                data-testid="inbox-detail-notes"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving} data-testid="inbox-detail-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-[#8C8078] mb-1 font-semibold">{title}</p>
      <div className="text-sm text-[#2C2420] whitespace-pre-wrap leading-relaxed">{children}</div>
    </div>
  );
}
